/**
 * Painel Pronto Atendimento: 1 agenda + switch de disponibilidade + fila.
 */
import { memo, useEffect, useState } from "react";
import {
  Box,
  ButtonBase,
  Chip,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronRight, EditOutlined, PlayArrow } from "@mui/icons-material";
import { toast } from "react-toastify";
import { useFluxPayContractContext } from "@/contexts/FluxPayContractContext";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { ViewHeader } from "../components/ViewHeader";
import { formatBrlInput } from "./moneyUtils";
import { ProntoAtendimentoAttendNowFlow } from "./ProntoAtendimentoAttendNowFlow";
import { ProntoAtendimentoConfigFlow } from "./ProntoAtendimentoConfigFlow";
import { ProntoAtendimentoQueueRow } from "./ProntoAtendimentoQueueRow";
import { useProntoAtendimentoConfigs } from "./useProntoAtendimentoConfigs";
import { useProntoAtendimentoQueue } from "./useProntoAtendimentoQueue";
import { PRONTO_ATENDIMENTO_MODE_LABELS } from "./types";

interface ScheduleProntoAtendimentoPanelProps {
  /** Quando false (troca de card na agenda), reseta diálogos abertos. */
  isActive: boolean;
}

function ScheduleProntoAtendimentoPanel({
  isActive,
}: ScheduleProntoAtendimentoPanelProps) {
  const {
    config,
    isAvailable,
    loading: configsLoading,
    saving,
    presencialLocations,
    upsertConfig,
    setAvailable,
  } = useProntoAtendimentoConfigs();

  const { userPractitionerRole } = usePractitionerRoleContext();
  const { hasFluxPayExtension } = useFluxPayContractContext();
  const hasSpecialty = Boolean(
    userPractitionerRole?.specialty?.some(
      (e) => e.coding?.[0]?.display || e.coding?.[0]?.code,
    ),
  );

  const {
    rows: queueRows,
    loading: queueLoading,
    refetch: refetchQueue,
  } = useProntoAtendimentoQueue({ enabled: isActive });

  const [attendOpen, setAttendOpen] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  /** true = aberto pelo switch ON (cancelar mantém OFF). */
  const [activateOnSave, setActivateOnSave] = useState(false);
  const [queueRefetchFlag, setQueueRefetchFlag] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setAttendOpen(false);
      setConfigOpen(false);
      setActivateOnSave(false);
    }
  }, [isActive]);

  useEffect(() => {
    if (queueRefetchFlag) {
      refetchQueue();
      setQueueRefetchFlag(false);
    }
  }, [queueRefetchFlag, refetchQueue]);

  const openEdit = () => {
    setActivateOnSave(false);
    setConfigOpen(true);
  };

  const handleSwitchChange = async (_e: unknown, checked: boolean) => {
    if (checked) {
      // Sem especialidade no perfil → bloqueia (padrão mental WarningSpecialtyDialog)
      if (!hasSpecialty) {
        toast.info(
          "Configure sua especialidade no perfil profissional antes de ativar o Pronto Atendimento.",
        );
        return;
      }
      // Não liga o switch ainda — abre config; cancelar = permanece OFF
      setActivateOnSave(true);
      setConfigOpen(true);
      return;
    }
    try {
      await setAvailable(false);
    } catch {
      // toast no hook
    }
  };

  const handleAttendClick = () => {
    if (!isAvailable || !config) {
      toast.info(
        "Ative o Pronto Atendimento (disponível para atender agora) antes de iniciar.",
      );
      return;
    }
    setAttendOpen(true);
  };

  const handleConfigClose = () => {
    setConfigOpen(false);
    setActivateOnSave(false);
  };

  const canAttend = isAvailable && !saving;

  // Chips das configs ativas (modo, especialidade, valor, local)
  const configChips: Array<{ label: string; title: string }> = [];
  if (config) {
    configChips.push({
      label: PRONTO_ATENDIMENTO_MODE_LABELS[config.mode],
      title: "Modo de atendimento",
    });
    const specialty =
      config.specialty || config.healthcareServiceDisplay || "";
    if (specialty) {
      configChips.push({ label: specialty, title: "Especialidade / serviço" });
    }
    if (hasFluxPayExtension && config.value > 0) {
      configChips.push({
        label: `R$ ${formatBrlInput(config.value)}`,
        title: "Valor do atendimento",
      });
    }
    if (config.locationDisplay) {
      configChips.push({
        label: config.locationDisplay,
        title: "Local do atendimento",
      });
    }
  }

  return (
    <Stack direction="column" width="100%" spacing={2}>
      <ViewHeader title="Pronto Atendimento" titleOnly />

      {/* Uma linha: Atender agora → configs → disponibilidade */}
      <Paper
        variant="outlined"
        sx={{
          px: 1.5,
          py: 1,
          borderColor: isAvailable ? "primary.main" : "divider",
          bgcolor: isAvailable ? "primary100.main" : "background.paper",
        }}
      >
        {configsLoading ? (
          <Stack alignItems="center" py={0.5}>
            <CircularProgress size={20} />
          </Stack>
        ) : (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
          >
            {/* Foco principal: iniciar atendimento */}
            <Tooltip
              title={
                canAttend
                  ? "Inicie um atendimento avulso com paciente na hora"
                  : "Ative a disponibilidade ao lado para começar"
              }
            >
              {/* span: Tooltip precisa de wrapper quando o alvo está disabled */}
              <span>
                <ButtonBase
                  onClick={handleAttendClick}
                  disabled={!canAttend}
                  sx={{
                    borderRadius: 2,
                    px: 1,
                    py: 0.75,
                    opacity: canAttend ? 1 : 0.55,
                    textAlign: "left",
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: canAttend
                          ? "primary.main"
                          : "action.disabledBackground",
                        color: canAttend
                          ? "primary.contrastText"
                          : "text.disabled",
                      }}
                    >
                      <PlayArrow fontSize="small" />
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} noWrap>
                      Atender agora
                    </Typography>
                    <ChevronRight
                      fontSize="small"
                      sx={{
                        color: canAttend ? "primary.main" : "text.disabled",
                      }}
                    />
                  </Stack>
                </ButtonBase>
              </span>
            </Tooltip>

            {/* Configurações selecionadas */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              sx={{ flex: 1, minWidth: 0 }}
            >
              {configChips.length > 0 ? (
                configChips.map((chip) => (
                  <Tooltip key={`${chip.title}-${chip.label}`} title={chip.title}>
                    <Chip
                     
                      label={chip.label}
                      variant="outlined"
                      sx={{
                        height: 26,
                        bgcolor: "background.paper",
                        borderColor: "divider",
                        maxWidth: 180,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Tooltip>
                ))
              ) : (
                <Typography variant="caption" color="text.secondary">
                  Nenhuma agenda ainda. Ative para configurar.
                </Typography>
              )}
            </Stack>

            {/* Disponibilidade + editar */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              flexShrink={0}
            >
              <Tooltip title="Disponível para atender agora">
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAvailable}
                      onChange={handleSwitchChange}
                      disabled={saving || configsLoading}
                      color="primary"
                     
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap>
                      {isAvailable ? "Disponível" : "Indisponível"}
                    </Typography>
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              </Tooltip>
              {config && (
                <Tooltip title="Editar configuração">
                  <IconButton
                   
                    aria-label="Editar configuração"
                    onClick={openEdit}
                    disabled={saving}
                  >
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        )}
      </Paper>

      <Stack spacing={1}>
        <Typography variant="subtitle1" fontWeight={600}>
          Próximos pacientes
        </Typography>

        {queueLoading ? (
          <Stack alignItems="center" py={3}>
            <CircularProgress size={28} />
          </Stack>
        ) : queueRows.length === 0 ? (
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderStyle: "dashed",
              textAlign: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Nenhum paciente na fila no momento. Use “Atender agora” para
              iniciar um atendimento avulso.
            </Typography>
          </Paper>
        ) : (
          queueRows.map((row) => (
            <ProntoAtendimentoQueueRow
              key={row.id}
              row={row}
              setRefetch={setQueueRefetchFlag}
            />
          ))
        )}
      </Stack>

      <ProntoAtendimentoAttendNowFlow
        open={attendOpen}
        onClose={() => setAttendOpen(false)}
        config={config}
        onSuccess={() => refetchQueue()}
      />

      <ProntoAtendimentoConfigFlow
        open={configOpen}
        onClose={handleConfigClose}
        config={config}
        saving={saving}
        presencialLocations={presencialLocations}
        activateOnSave={activateOnSave}
        onSave={upsertConfig}
      />
    </Stack>
  );
}

export default memo(ScheduleProntoAtendimentoPanel);
